import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useRef, useState, useCallback } from "react";
import { $getSelection, $isRangeSelection } from "lexical";

export default function CustomTypeaheadPlugin({
	triggerFn,
	onQueryChange,
	onSelectOption,
	options,
	menuRenderFn,
	preselectFirstItem = true,
}) {
	const [editor] = useLexicalComposerContext();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [queryData, setQueryData] = useState(null);
	const anchorRef = useRef(null);

	// update query and create hidden span at caret
	useEffect(() => {
		const unregister = editor.registerUpdateListener(({ editorState }) => {
			editorState.read(() => {
				const selection = $getSelection();
				if (!$isRangeSelection(selection)) return;

				const node = selection.anchor.getNode();
				const text = node.getTextContent();
				const match = triggerFn(text);

				if (match) {
					setQueryData(match);
					onQueryChange(match.matchingString);

					if (preselectFirstItem) setSelectedIndex(0);

					// Create hidden span at caret if it doesn't exist
					if (!anchorRef.current) {
						const span = document.createElement("span");
						span.style.position = "absolute";
						span.style.width = "1px";
						span.style.height = "1px";
						span.style.pointerEvents = "none";
						span.style.opacity = "0";
						document.body.appendChild(span);
						anchorRef.current = span;
					}
				} else {
					setQueryData(null);
					onQueryChange(null);
					setSelectedIndex(0);
					if (anchorRef.current) {
						document.body.removeChild(anchorRef.current);
						anchorRef.current = null;
					}
				}
			});
		});
		return () => unregister();
	}, [editor, triggerFn, onQueryChange, preselectFirstItem]);

	// keyboard navigation
	const handleKeyDown = useCallback(
		(e) => {
			if (!queryData || !options.length) return;

			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((prev) => Math.min(prev + 1, options.length - 1));
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((prev) => Math.max(prev - 1, 0));
			} else if (e.key === "Enter") {
				e.preventDefault();
				const selectedOption = options[selectedIndex];
				if (selectedOption) {
					onSelectOption(selectedOption, queryData.nodeToReplace, () => {
						setQueryData(null);
						setSelectedIndex(0);
						if (anchorRef.current) {
							document.body.removeChild(anchorRef.current);
							anchorRef.current = null;
						}
					});
				}
			} else if (e.key === "Escape") {
				e.preventDefault();
				setQueryData(null);
				setSelectedIndex(0);
				if (anchorRef.current) {
					document.body.removeChild(anchorRef.current);
					anchorRef.current = null;
				}
			}
		},
		[queryData, options, selectedIndex, onSelectOption]
	);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	if (!queryData || !anchorRef.current) return null;

	return menuRenderFn(
		anchorRef,
		{
			selectedIndex,
			setHighlightedIndex: setSelectedIndex,
			selectOptionAndCleanUp: (option) => {
				onSelectOption(option, queryData.nodeToReplace, () => {
					setQueryData(null);
					setSelectedIndex(0);
					if (anchorRef.current) {
						document.body.removeChild(anchorRef.current);
						anchorRef.current = null;
					}
				});
			},
		}
	);
}
